import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiAward, FiPlus, FiX, FiCheck } from 'react-icons/fi';
import { db, auth } from '../../firebase';
import { doc, getDoc, collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore'; // MÓDOSÍTVA

const CreateTournament = () => {
  const navigate = useNavigate();

  // --- ÁLLAPOTOK ---
  const [tournamentName, setTournamentName] = useState('');
  const [organizers, setOrganizers] = useState([]); // Tömb: { id, name, status }
  const [showClubSearch, setShowClubSearch] = useState(false);
  const [clubSearchTerm, setClubSearchTerm] = useState('');
  const [clubSuggestions, setClubSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false); // ÚJ: Keresés töltés jelző
  const [roundCount, setRoundCount] = useState('');
  const [scoringRule, setScoringRule] = useState('best_two');
  const [tieBreakerRule, setTieBreakerRule] = useState('shoot_off'); // ÚJ
  const [minParticipation, setMinParticipation] = useState('');
  const [mandatoryFinal, setMandatoryFinal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- KLUB NÉV FORMÁZÓ SEGÉDFÜGGVÉNY ---
  const formatClubName = (name) => {
    if (!name || name === "Egyéb") return name;
    const specialCases = {
        "3d": "3D", "kft.": "Kft.", "se": "SE", "sk": "SK", "dse": "DSE",
        "kse": "KSE", "mavir": "MAVIR", "ifaa": "IFAA", "hdh-iaa": "HDH-IAA"
    };

    return name.split(' ').map(word => {
      const cleanWord = word.replace(/[^a-zA-ZáéíóöőúüűÁÉÍÓÖŐÚÜŰ0-9]/g, '').toLowerCase();
      if (specialCases[cleanWord]) return specialCases[cleanWord];
      if (word.startsWith('"')) {
          return '"' + (word.charAt(1)?.toUpperCase() || "") + word.slice(2).toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // --- BEJELENTKEZETT KLUB BETÖLTÉSE ---
  useEffect(() => {
    const fetchClubProfile = async () => {
      if (auth.currentUser) {
        try {
          const clubRef = doc(db, "Clubs", auth.currentUser.uid);
          const clubSnap = await getDoc(clubRef);
          if (clubSnap.exists()) {
            const data = clubSnap.data();
            const clubName = data.clubName || data.fullName || 'Saját Egyesület';
            // Az első (tulajdonos) egyesület beállítása objektumként
            setOrganizers([{
              id: auth.currentUser.uid,
              name: formatClubName(clubName),
              status: 'owner'
            }]);
          }
        } catch (error) {
          console.error("Hiba a klub adatainak lekérésekor:", error);
        }
      }
    };
    fetchClubProfile();
  }, []);

  // --- FIRESTORE-ALAPÚ EGYESÜLET KERESŐ LOGIKA ---
  const handleClubSearch = async (value) => {
    setClubSearchTerm(value);

    if (value.length >= 2) {
      setIsSearching(true);
      try {
        const clubsRef = collection(db, "Clubs");
        const querySnapshot = await getDocs(clubsRef); // Kisebb adatbázisnál ez gyors, de később algolia/typesense érdemes

        const suggestions = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const nameToSearch = (data.clubName || data.fullName || "").toLowerCase();
          // Szűrjük a saját klubot, és csak azokat adjuk vissza, amiben benne van a keresett szó
          if (doc.id !== auth.currentUser?.uid && nameToSearch.includes(value.toLowerCase())) {
            suggestions.push({
              id: doc.id,
              name: formatClubName(data.clubName || data.fullName)
            });
          }
        });

        // Csak a top 10 találat
        setClubSuggestions(suggestions.slice(0, 10));
      } catch (error) {
        console.error("Hiba a klubok keresésekor:", error);
      } finally {
        setIsSearching(false);
      }
    } else {
      setClubSuggestions([]);
    }
  };

  const handleAddOrganizer = (clubObj) => {
    // Ellenőrizzük, hogy nincs-e már benne ID alapján
    if (!organizers.some(org => org.id === clubObj.id)) {
      setOrganizers([...organizers, { ...clubObj, status: 'pending' }]);
    }
    setClubSearchTerm('');
    setClubSuggestions([]);
    setShowClubSearch(false);
  };

  const handleRemoveOrganizer = (indexToRemove) => {
    setOrganizers(organizers.filter((_, index) => index !== indexToRemove));
  };

  // --- MENTÉS LOGIKA ---
  const handleCreate = async () => {
    if (!tournamentName.trim()) {
      alert("A kupasorozat megnevezése kötelező!");
      return;
    }

    if (!auth.currentUser) {
      alert("Nincs bejelentkezett felhasználó!");
      return;
    }

    setIsSubmitting(true);

    try {
      const finalRoundCount = parseInt(roundCount) || 0;

      // ÚJ: Generáljuk le az okos fordulókat
      const initialRounds = [];
      for (let i = 0; i < finalRoundCount; i++) {
        initialRounds.push({
          roundIndex: i,
          organizerId: auth.currentUser.uid, // Alapértelmezett: a létrehozó
          eventId: null // Még nincs hozzárendelve konkrét verseny
        });
      }

      const tournamentData = {
        name: tournamentName,
        organizers: organizers,
        roundCount: finalRoundCount,
        minParticipation: parseInt(minParticipation) || 0,
        scoringRule: scoringRule,
        tieBreakerRule: tieBreakerRule, // ÚJ
        mandatoryFinal: mandatoryFinal,
        organizerId: auth.currentUser.uid,
        status: 'Aktív',
        rounds: initialRounds, // Ezentúl okos tömb
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, "tournaments"), tournamentData);

      navigate('/egyesulet-dashboard');
    } catch (error) {
      console.error("Hiba a mentéskor:", error);
      alert("Hiba történt a kupasorozat létrehozása közben.");
      setIsSubmitting(false);
    }
  };

  // --- STÍLUSOK ---
  const pageContainerStyle = {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f0f2f5',
    minHeight: 'calc(100vh - 4rem)',
    maxWidth: '800px',
    margin: '0 auto'
  };

  const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '20px',
    padding: 0
  };

  const mainCardStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '30px 40px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    textAlign: 'left'
  };

  const inputGroupStyle = {
    marginBottom: '25px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: '#b30000',
    marginBottom: '10px',
    textTransform: 'uppercase'
  };

  const formInputStyle = {
    width: '100%',
    padding: '12px 15px',
    borderRadius: '10px',
    border: '1px solid #ddd',
    fontSize: '1.1rem',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: '#fcfcfc'
  };

  const autocompleteContainerStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
    zIndex: 100,
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: '5px'
  };

  const autocompleteItemStyle = {
    padding: '10px 15px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    fontSize: '0.95rem',
    color: '#333'
  };

  return (
    <div style={pageContainerStyle}>
      <button onClick={() => navigate('/egyesulet-dashboard')} style={backButtonStyle}>
        <FiArrowLeft /> Vissza az Irányítópultra
      </button>

      <div style={mainCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px', borderBottom: '2px solid #eee', paddingBottom: '15px' }}>
          <FiAward size={40} color="#b30000" />
          <h2 style={{ color: '#b30000', margin: 0 }}>Kupasorozat Létrehozása</h2>
        </div>

        {/* 1. KUPASOROZAT NEVE */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Kupasorozat Neve</label>
          <input
            type="text"
            style={{ ...formInputStyle, fontSize: '1.3rem', fontWeight: 'bold' }}
            placeholder="Pl. Észak-Alföldi Régiós Bajnokság 2026"
            value={tournamentName}
            onChange={(e) => setTournamentName(e.target.value)}
          />
        </div>

        {/* 2. SZERVEZŐ EGYESÜLETEK */}
        <div style={inputGroupStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Szervező Egyesület(ek)</label>
            {!showClubSearch && (
              <button
                onClick={() => setShowClubSearch(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1877F2',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <FiPlus /> Társrendező hozzáadása
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: showClubSearch ? '15px' : '0' }}>
            {organizers.map((org, index) => (
              <div key={org.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                padding: '12px 15px',
                borderRadius: '8px',
                border: '1px solid #eee'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{org.name}</span>
                  {org.status === 'owner' && (
                    <span style={{ fontSize: '0.7rem', backgroundColor: '#e6fffa', color: '#2c7a7b', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Alapító</span>
                  )}
                  {org.status === 'pending' && (
                    <span style={{ fontSize: '0.7rem', backgroundColor: '#fffaf0', color: '#b7791f', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Meghívott</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {org.status !== 'owner' && (
                    <button
                      onClick={() => handleRemoveOrganizer(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#b30000',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '5px'
                      }}
                      title="Eltávolítás"
                    >
                      <FiX size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* KERESŐ MEZŐ */}
          {showClubSearch && (
            <div style={{ position: 'relative', marginTop: '10px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  style={formInputStyle}
                  placeholder="Kezdd el gépelni az egyesület nevét..."
                  value={clubSearchTerm}
                  onChange={(e) => handleClubSearch(e.target.value)}
                  autoFocus
                />
                <button
                  onClick={() => {
                    setShowClubSearch(false);
                    setClubSearchTerm('');
                    setClubSuggestions([]);
                  }}
                  style={{
                    backgroundColor: '#eee',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '0 15px',
                    cursor: 'pointer',
                    color: '#333'
                  }}
                >
                  Mégse
                </button>
              </div>

              {/* AUTOCOMPLETE JAVASLATOK (FIRESTORE) */}
              {isSearching ? (
                <div style={{...autocompleteContainerStyle, padding: '15px', textAlign: 'center', color: '#888', fontStyle: 'italic'}}>
                  Keresés az adatbázisban...
                </div>
              ) : clubSuggestions.length > 0 ? (
                <div style={autocompleteContainerStyle}>
                  {clubSuggestions.map((club) => (
                    <div
                      key={club.id}
                      style={autocompleteItemStyle}
                      onClick={() => handleAddOrganizer(club)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {club.name}
                    </div>
                  ))}
                </div>
              ) : (clubSearchTerm.length >= 2 && !isSearching) ? (
                <div style={{...autocompleteContainerStyle, padding: '15px', textAlign: 'center', color: '#888', fontStyle: 'italic'}}>
                  Nincs találat. (Csak a regisztrált klubok közül lehet választani!)
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* 3. FORDULÓK ÉS MINIMUM RÉSZVÉTEL */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px' }}>
          <div>
            <label style={labelStyle}>Fordulók száma</label>
            <input
              type="number"
              min="1"
              max="20"
              style={formInputStyle}
              placeholder="Pl. 3"
              value={roundCount}
              onChange={(e) => setRoundCount(e.target.value)}
            />
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
              Hány állomásból fog állni?
            </p>
          </div>

          <div>
            <label style={labelStyle}>Minimum részvétel</label>
            <input
              type="number"
              min="1"
              max="20"
              style={formInputStyle}
              placeholder="Pl. 2"
              value={minParticipation}
              onChange={(e) => setMinParticipation(e.target.value)}
            />
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
              Hány versenyen kell részt venni a végleges értékeléshez?
            </p>
          </div>
        </div>

        {/* 4. ÖSSZESÍTÉSI SZABÁLY */}
        <div style={{ ...inputGroupStyle, marginBottom: '25px' }}>
          <label style={labelStyle}>Összesítési Szabály</label>
          <select
            style={{ ...formInputStyle, cursor: 'pointer' }}
            value={scoringRule}
            onChange={(e) => setScoringRule(e.target.value)}
          >
            <option value="best_two">Legjobb két verseny pontszáma összeadódik</option>
          </select>
          <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
            Hogyan dől el a végső sorrend az értékelhető íjászok között?
          </p>
        </div>

        {/* ÚJ: DÖNTETLEN SZABÁLY */}
        <div style={{ ...inputGroupStyle, marginBottom: '25px' }}>
          <label style={labelStyle}>Döntetlen esetén:</label>
          <select
            style={{ ...formInputStyle, cursor: 'pointer' }}
            value={tieBreakerRule}
            onChange={(e) => setTieBreakerRule(e.target.value)}
          >
            <option value="shoot_off">Szétlövés</option>
          </select>
          <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
            Mi történik, ha két versenyzőnek azonos az összesített pontszáma?
          </p>
        </div>

        {/* 5. KÖTELEZŐ DÖNTŐ CHECKBOX */}
        <div style={{ ...inputGroupStyle, backgroundColor: '#fcfcfc', border: '1px solid #eee', padding: '15px', borderRadius: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={mandatoryFinal}
              onChange={(e) => setMandatoryFinal(e.target.checked)}
              style={{ width: '20px', height: '20px', accentColor: '#b30000', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 'bold', color: '#333', fontSize: '1rem' }}>
                Az utolsó fordulón (Döntő) való részvétel kötelező az összesítéshez.
              </span>
              <span style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                Ha be van pipálva, a versenyző nem kaphat végső értékelést (érmet), ha nem jelenik meg az utolsó állomáson.
              </span>
            </div>
          </label>
        </div>

        {/* --- LÉTREHOZÁS GOMB --- */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '40px' }}>
          <button
            onClick={handleCreate}
            disabled={isSubmitting}
            style={{
              backgroundColor: '#b30000',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '10px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '1.1rem',
              boxShadow: '0 4px 10px rgba(179,0,0,0.2)',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {isSubmitting ? 'Létrehozás...' : (
              <>
                <FiCheck size={24} />
                Kupasorozat Létrehozása
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CreateTournament;